/**
 * Plantillas para difusiones de correo.
 *
 * El correo final = MARCO de marca (header navy + footer, NO editable)
 * + CONTENIDO (el área que el usuario edita visualmente, sin ver HTML).
 * `buildEmailHtml(inner)` arma el HTML completo email-safe al enviar.
 */

export interface EmailTemplate {
  key: string;
  name: string;
  description: string;
  /** Variables sugeridas que usa la plantilla (el Excel debe traerlas). */
  suggestedVars: string[];
  subject: string;
  /** Contenido editable (va dentro del marco de marca). */
  inner: string;
}

const NAVY = '#0A1931';
const BLUE = '#1363DA';
const CYAN = '#47B5FF';

/** Envuelve el contenido editable en el marco de marca (header + footer). */
export function buildEmailHtml(inner: string): string {
  return `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:#F1F5F9;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#F1F5F9;padding:24px 12px;">
<tr><td align="center">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background-color:#FFFFFF;border-radius:12px;overflow:hidden;border:1px solid #E2E8F0;">
  <tr>
    <td style="background-color:${NAVY};padding:22px 32px;">
      <span style="font-family:Arial,Helvetica,sans-serif;font-size:20px;font-weight:bold;color:#FFFFFF;letter-spacing:1px;">ISOFT <span style="color:${CYAN};">People</span></span>
    </td>
  </tr>
  <tr><td style="height:4px;background:linear-gradient(90deg,${NAVY},${BLUE},${CYAN});font-size:0;line-height:0;">&nbsp;</td></tr>
  <tr>
    <td style="padding:32px;font-family:Arial,Helvetica,sans-serif;color:#0F172A;font-size:15px;line-height:1.6;">
${inner}
    </td>
  </tr>
  <tr>
    <td style="padding:18px 32px;background-color:#F8FAFC;border-top:1px solid #E2E8F0;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#94A3B8;">
      Este es un correo interno de ISOFT. Si tienes dudas escribe a <a href="mailto:soporte@isoft-ste.com" style="color:${BLUE};text-decoration:none;">soporte@isoft-ste.com</a>.<br>
      ISOFT &copy; 2026 &middot; <a href="https://people.isoft-ste.com" style="color:${BLUE};text-decoration:none;">people.isoft-ste.com</a>
    </td>
  </tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    key: 'credenciales',
    name: 'Bienvenida con credenciales',
    description: 'Acceso al sistema: usuario y contraseña inicial para cada colaborador.',
    suggestedVars: ['CORREO', 'NOMBRE', 'PASS'],
    subject: 'Bienvenido a ISOFT People — tus credenciales de acceso',
    inner: `      <h1 style="margin:0 0 16px;font-size:22px;color:${NAVY};">Estimad(@) {{NOMBRE}}</h1>
      <p style="margin:0 0 16px;">Te damos la bienvenida a <strong>ISOFT People</strong>, la plataforma interna de gestión de Recursos Humanos.</p>
      <p style="margin:0 0 8px;">Tus credenciales de acceso son:</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:8px 0 20px;">
        <tr><td style="background-color:#EBF2FF;border:1px solid #BFDBFE;border-radius:10px;padding:18px 22px;">
          <p style="margin:0 0 6px;font-size:13px;color:#64748B;">Usuario</p>
          <p style="margin:0 0 14px;font-size:16px;font-weight:bold;color:${NAVY};">{{CORREO}}</p>
          <p style="margin:0 0 6px;font-size:13px;color:#64748B;">Contrase&ntilde;a temporal</p>
          <p style="margin:0;font-size:20px;font-weight:bold;letter-spacing:1px;color:${BLUE};font-family:Courier,monospace;">{{PASS}}</p>
        </td></tr>
      </table>
      <p style="margin:0 0 24px;">Al ingresar por primera vez el sistema te pedir&aacute; cambiar la contrase&ntilde;a.</p>
      <table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 0 8px;">
        <tr><td style="background-color:${BLUE};border-radius:8px;">
          <a href="https://people.isoft-ste.com" style="display:inline-block;padding:13px 28px;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:bold;color:#FFFFFF;text-decoration:none;">Ingresar a ISOFT People</a>
        </td></tr>
      </table>`,
  },
  {
    key: 'anuncio',
    name: 'Anuncio general',
    description: 'Comunicado corporativo para todos los colaboradores.',
    suggestedVars: ['CORREO', 'NOMBRE'],
    subject: 'Comunicado ISOFT',
    inner: `      <h1 style="margin:0 0 16px;font-size:22px;color:${NAVY};">Hola {{NOMBRE}}</h1>
      <p style="margin:0 0 16px;">Escribe aqu&iacute; el contenido de tu comunicado. Puedes usar <strong>negritas</strong>, listas y enlaces.</p>
      <ul style="margin:0 0 16px;padding-left:20px;">
        <li style="margin-bottom:6px;">Punto importante uno</li>
        <li style="margin-bottom:6px;">Punto importante dos</li>
      </ul>
      <p style="margin:0;">Saludos,<br><strong>Equipo ISOFT</strong></p>`,
  },
  {
    key: 'recordatorio',
    name: 'Recordatorio',
    description: 'Aviso corto con un llamado a la acción.',
    suggestedVars: ['CORREO', 'NOMBRE'],
    subject: 'Recordatorio — ISOFT People',
    inner: `      <h1 style="margin:0 0 16px;font-size:22px;color:${NAVY};">Hola {{NOMBRE}}</h1>
      <p style="margin:0 0 20px;">Te recordamos que tienes una acci&oacute;n pendiente en la plataforma.</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 20px;">
        <tr><td style="background-color:#FFF7ED;border-left:4px solid #F59E0B;border-radius:6px;padding:14px 18px;font-size:14px;color:#7C2D12;">
          Describe aqu&iacute; el recordatorio (fecha l&iacute;mite, requisito, documento, etc.).
        </td></tr>
      </table>
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr><td style="background-color:${BLUE};border-radius:8px;">
          <a href="https://people.isoft-ste.com" style="display:inline-block;padding:12px 26px;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;color:#FFFFFF;text-decoration:none;">Ir a la plataforma</a>
        </td></tr>
      </table>`,
  },
  {
    key: 'blanco',
    name: 'En blanco',
    description: 'Sólo el marco con la marca ISOFT; escribe el contenido desde cero.',
    suggestedVars: ['CORREO'],
    subject: '',
    inner: `      <p style="margin:0;">Escribe aqu&iacute; tu mensaje...</p>`,
  },
];
